import type { NewPendingMessage } from "@/components/ai/chat/ChatInput";
import type { Message } from "@/hooks/use-stream-conversation";
import { useSendToAgentExecution } from "@/queries/agent-queries";
import { useCallback, useRef } from "react";
import {
    SendToAgentExecutionRequest,
    UserInputBlock,
    UserInputBlock_Input,
    UserInputBlock_TextInput,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import { v4 as uuidv4 } from "uuid";

/**
 * Sends a text message to an agent execution with optimistic UI updates.
 * Used by interactive blocks (clarifying questions, next steps) that need
 * to send messages outside the normal ChatInput flow.
 */
export function useSendInteractiveMessage(
    agentExecutionId: string | undefined,
    onNewMessage: (message: NewPendingMessage, messages: Message[]) => void,
    messages: Message[],
) {
    const sendToAgentExecution = useSendToAgentExecution();
    const messagesRef = useRef(messages);
    messagesRef.current = messages;

    const sendMessage = useCallback(
        async (text: string) => {
            if (!agentExecutionId || !text.trim()) {
                return;
            }

            const messageId = uuidv4();
            const currentMessages = messagesRef.current;

            onNewMessage({ id: messageId, content: text }, currentMessages);

            try {
                await sendToAgentExecution.mutateAsync(
                    new SendToAgentExecutionRequest({
                        agentExecutionId,
                        userInput: new UserInputBlock({
                            id: messageId,
                            inputs: [
                                new UserInputBlock_Input({
                                    input: {
                                        case: "text",
                                        value: new UserInputBlock_TextInput({
                                            content: text,
                                        }),
                                    },
                                }),
                            ],
                        }),
                    }),
                );
            } catch (error) {
                onNewMessage({ id: messageId }, currentMessages);
                throw error;
            }
        },
        [agentExecutionId, sendToAgentExecution, onNewMessage],
    );

    return sendMessage;
}
